import {
    createDataMapper,
    MapperKeys,
    transformSourceToTargetKeys,
} from '@common/utils/query/dataMappers';
import { isEmpty } from '@common/utils/typeGuards';
import {
    BackendCollection,
    BackendCollectionCustomFields,
    Collection,
    CollectionTechnicalDetails,
    DEFAULT_COLLECTION_TECHNICAL_DETAILS,
    MetricDataPoint,
    MetricsCollection,
} from './types';

export const COLLECTION_KEYS: MapperKeys<BackendCollection, Collection> = {
    id: 'id',
    categoryId: 'categoryId',
    proscopeId: 'id',
    productionId: 'productionId',
    title: 'title',
    created: 'created',
    updated: 'lastUpdated',
    version: 'version',
};

function parseIdAndTitle(input: string): { title: string; id: string } {
    const regexp = /^\[(.*?)]\s*(.*)$|^\{-(.*?)-}\s*(.*)$/;

    const match = input.match(regexp);

    if (!match) return { title: input, id: input };

    let id: string;
    let rawTitle: string;

    if (match[1] !== undefined) {
        id = match[1];
        rawTitle = match[2];
    } else if (match[3] !== undefined) {
        id = match[3];
        rawTitle = match[4];
    } else {
        return { title: input, id: input };
    }

    const title = rawTitle
        .replace(/\.$/, '') // remove . at the end
        .replace(/^[^a-zA-Z0-9]+/, '') // special chars at start
        .replace(/(.*)\s*(?:\(|\/)(.{1,3})(?:\)|\/)$/, '$2 $1') // Title /(1-3chars)/ => 1-3chars Title
        .trim();

    return { title, id };
}

const parseArray = (val: string | undefined, fallback: string[]): string[] =>
    val ? val.split(/[ |]/).filter(Boolean) : fallback;

const parseNumber = (
    val: string | undefined,
    fallback: number | string,
): number | string =>
    val ? (isNaN(Number(val)) ? val : Number(val)) : fallback;

const parseBoolean = (val: string | undefined, fallback: boolean): boolean =>
    val ? val.toLowerCase() === 'true' || val === '1' : fallback;

const parseTechnicalDetails = (
    customFields: BackendCollectionCustomFields | undefined,
): CollectionTechnicalDetails | undefined => {
    if (!customFields || isEmpty(customFields)) return undefined;
    const defaults = DEFAULT_COLLECTION_TECHNICAL_DETAILS;

    return {
        aspectRatios: parseArray(
            customFields.aspectRatios?.value,
            defaults.aspectRatios,
        ),
        audioLanguages: parseArray(
            customFields.audioLanguages?.value,
            defaults.audioLanguages,
        ),
        colorSpaces: parseArray(
            customFields.colorSpaces?.value,
            defaults.colorSpaces,
        ),
        deliverables: parseArray(
            customFields.deliverables?.value,
            defaults.deliverables,
        ),
        resolutions: parseArray(
            customFields.resolutions?.value,
            defaults.resolutions,
        ),

        volume: parseNumber(customFields.volume?.value, defaults.volume),
        assetCount: parseNumber(
            customFields.assetCount?.value,
            defaults.assetCount,
        ),

        stl: parseBoolean(customFields.stl?.value, defaults.stl),
    };
};

const mapBackendCollectionToFrontendCollection = (
    backendCollection: BackendCollection,
): Collection => {
    const parsedIdAndTitle = parseIdAndTitle(backendCollection.title);

    const technicalDetails = parseTechnicalDetails(
        backendCollection.customFields,
    );

    const transformedKeys = transformSourceToTargetKeys(
        backendCollection,
        COLLECTION_KEYS,
    );

    return {
        ...transformedKeys,
        proscopeId: parsedIdAndTitle.id,
        title: parsedIdAndTitle.title,
        technicalDetails,
    };
};

export const transformCollection = createDataMapper<
    BackendCollection,
    Collection
>(mapBackendCollectionToFrontendCollection, true);

export const DEFAULT_METRICS_COLLECTION: MetricsCollection = {
    lastAnalysis: '',
    volume: 0,
    assetCount: 0,
    offerVolume: [],
    offerCount: [],
    categoryVolume: [],
    categoryCount: [],
};

const extractMetrics = (
    fields: BackendCollectionCustomFields,
    prefix: string,
): MetricDataPoint[] =>
    Object.entries(fields)
        .filter(
            ([key, field]) => key.startsWith(prefix) && field?.value != null,
        )
        .map(([key, field]) => ({
            name: key.replace(prefix, ''),
            value: Number(field?.value) || 0,
        }));

const mapBackendCollectionToMetricsCollection = (
    backendCollection: BackendCollection | undefined,
): MetricsCollection => {
    const customFields = backendCollection?.customFields;

    if (!customFields || isEmpty(customFields)) {
        return DEFAULT_METRICS_COLLECTION;
    }

    const { lastAnalysis, volume, assetCount } = customFields;

    return {
        offerVolume: extractMetrics(customFields, 'offer_volume_'),
        offerCount: extractMetrics(customFields, 'offer_count_'),
        categoryVolume: extractMetrics(customFields, 'category_volume_'),
        categoryCount: extractMetrics(customFields, 'category_count_'),
        lastAnalysis: lastAnalysis?.value ?? '',
        volume: Number(volume?.value ?? 0),
        assetCount: Number(assetCount?.value ?? 0),
    };
};

export const transformMetricsCollection = createDataMapper<
    BackendCollection | undefined,
    MetricsCollection
>(mapBackendCollectionToMetricsCollection, false);
